import { Divider, Stack } from '@mui/material';
import { z } from 'zod';

import { api } from 'common';

import { OrderJobParamsFormFields, OrderJobParamsSchema } from './Common/Orders';
import {
  type AsynchronousJobVariant,
  NullableStringSchema,
  OptionalStringSchema,
  RequiredStringSchema,
} from './common';
import { BlockValueTemplateField } from '../../../ConfigFields/CodeMirror/FlowTemplate';

import JobKind = api.RpaJob.Kind;

export const ExportNoticeOfAdmissionVariant: AsynchronousJobVariant = {
  schema: z.object({
    jobKind: z.literal(JobKind.ExportNoticeOfAdmission),
    payload: OrderJobParamsSchema.merge(
      z.object({
        payor: OptionalStringSchema,
        payorPlan: OptionalStringSchema,
        specialty: OptionalStringSchema,
        patient: z.object({
          firstName: RequiredStringSchema,
          lastName: RequiredStringSchema,
          dateOfBirth: RequiredStringSchema,
        }),
        insurance: z
          .object({
            memberId: OptionalStringSchema,
          })
          .optional()
          .nullable(),
        submission: z.object({
          diagnoses: RequiredStringSchema,
          reasonForExam: OptionalStringSchema,
          referredByProvider: z.object({
            firstName: RequiredStringSchema,
            lastName: RequiredStringSchema,
            npi: RequiredStringSchema,
            phoneNumber: RequiredStringSchema,
          }),
          providers: RequiredStringSchema,
          practice: z.object({
            taxId: RequiredStringSchema,
            phoneNumber: RequiredStringSchema,
            address: z.object({
              address: NullableStringSchema,
              city: NullableStringSchema,
              state: RequiredStringSchema,
              zip: NullableStringSchema,
            }),
          }),
          serviceDate: RequiredStringSchema,
          ehrScheduled: NullableStringSchema,
          serviceReviewCategory: RequiredStringSchema,
          serviceType: RequiredStringSchema,
          serviceFacilityType: RequiredStringSchema,
          levelOfService: RequiredStringSchema,
          admissionType: RequiredStringSchema,
          admissionSource: NullableStringSchema,
          referredByDepartment: NullableStringSchema,
          referredToDepartment: NullableStringSchema,
          patientPhoneNumber: NullableStringSchema,
        }),
      })
    ),
  }),
  Config: () => {
    return (
      <Stack spacing={2}>
        {/* Payor Information */}
        <BlockValueTemplateField
          fieldName="config.payload.payor"
          label="Payor"
          helperText="Enter the reference to the payor."
          placeholder="e.g. {{order.data.payor}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.payorPlan"
          label="Payor Plan"
          helperText="Enter the reference to the payor plan."
          placeholder="e.g. {{order.data.payorPlan}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.specialty"
          label="Specialty"
          helperText="Enter the reference to the specialty."
          placeholder="e.g. {{order.data.specialty}}"
        />

        <Divider />

        {/* Patient Information */}
        <BlockValueTemplateField
          fieldName="config.payload.patient.firstName"
          label="Patient First Name"
          helperText="Enter the reference to the patient's first name."
          placeholder="e.g. {{patient.data.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patient.lastName"
          label="Patient Last Name"
          helperText="Enter the reference to the patient's last name."
          placeholder="e.g. {{patient.data.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.patient.dateOfBirth"
          label="Patient Date of Birth"
          helperText="Enter the reference to the patient's date of birth."
          placeholder="e.g. {{patient.data.dateOfBirth}}"
        />

        <Divider />

        {/* Insurance Information */}
        <BlockValueTemplateField
          fieldName="config.payload.insurance.memberId"
          label="Insurance Member ID"
          helperText="Enter the reference to the insurance member ID."
          placeholder="e.g. {{order.data.insurance.memberId}}"
        />

        <Divider />

        {/* Diagnoses */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.diagnoses"
          label="Diagnoses"
          helperText="Enter the reference to the diagnoses as an interpolated string."
          placeholder="e.g. {{order.data.submission.diagnoses}}"
        />

        <Divider />

        {/* Providers */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.providers"
          label="Providers with role type"
          helperText="Enter the reference to the providers as an interpolated string."
          placeholder="e.g. {{order.data.submission.providers}}"
        />

        <Divider />

        {/* Reason for Exam */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.reasonForExam"
          label="Reason for Exam"
          helperText="Enter the reference to the reason for exam."
          placeholder="e.g. {{order.data.submission.reasonForExam}}"
        />

        <Divider />

        {/* Referred By Provider */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.firstName"
          label="Referred By Provider First Name"
          helperText="Enter the reference to the referring provider's first name."
          placeholder="e.g. {{order.data.submission.referredByProvider.firstName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.lastName"
          label="Referred By Provider Last Name"
          helperText="Enter the reference to the referring provider's last name."
          placeholder="e.g. {{order.data.submission.referredByProvider.lastName}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.npi"
          label="Referred By Provider NPI"
          helperText="Enter the reference to the referring provider's NPI."
          placeholder="e.g. {{order.data.submission.referredByProvider.npi}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByProvider.phoneNumber"
          label="Referred By Provider Phone Number"
          helperText="Enter the reference to the referring provider's phone number."
          placeholder="e.g. {{order.data.submission.referredByProvider.phoneNumber}}"
        />

        <Divider />

        {/* Practice Information */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.taxId"
          label="Practice Tax ID"
          helperText="Enter the reference to the practice's Tax ID."
          placeholder="e.g. {{order.data.submission.practice.taxId}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.phoneNumber"
          label="Practice Phone Number"
          helperText="Enter the reference to the practice's phone number."
          placeholder="e.g. {{order.data.submission.practice.phoneNumber}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.address"
          label="Practice Address"
          helperText="Enter the reference to the practice's address."
          placeholder="e.g. {{order.data.submission.practice.address.address}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.city"
          label="Practice City"
          helperText="Enter the reference to the practice's city."
          placeholder="e.g. {{order.data.submission.practice.address.city}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.state"
          label="Practice State"
          helperText="Enter the reference to the practice's state."
          placeholder="e.g. {{order.data.submission.practice.address.state}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.practice.address.zip"
          label="Practice ZIP Code"
          helperText="Enter the reference to the practice's ZIP code."
          placeholder="e.g. {{order.data.submission.practice.address.zip}}"
        />

        <Divider />

        {/* Admission Details */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.serviceDate"
          label="Service Date"
          helperText="Enter the reference to the service date."
          placeholder="e.g. {{order.data.submission.serviceDate}}"
        />

        <BlockValueTemplateField
          fieldName="config.payload.submission.serviceReviewCategory"
          label="Service Review Category"
          helperText="Enter the reference to the service review category."
          placeholder="e.g. {{order.data.submission.serviceReviewCategory}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.serviceType"
          label="Service Type"
          helperText="Enter the reference to the service type."
          placeholder="e.g. {{order.data.submission.serviceType}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.serviceFacilityType"
          label="Service Facility Type"
          helperText="Enter the reference to the service facility type."
          placeholder="e.g. {{order.data.submission.serviceFacilityType}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.levelOfService"
          label="Level of Service"
          helperText="Enter the reference to the level of service."
          placeholder="e.g. {{order.data.submission.levelOfService}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.admissionType"
          label="Admission Type"
          helperText="Enter the reference to the admission type."
          placeholder="e.g. {{order.data.submission.admissionType}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.admissionSource"
          label="Admission Source"
          helperText="Enter the reference to the admission source."
          placeholder="e.g. {{order.data.submission.admissionSource}}"
        />

        <Divider />

        {/* Departments */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredByDepartment"
          label="Referred By Department"
          helperText="Enter the reference to the referring department."
          placeholder="e.g. {{order.data.submission.referredByDepartment}}"
        />
        <BlockValueTemplateField
          fieldName="config.payload.submission.referredToDepartment"
          label="Referred To Department"
          helperText="Enter the reference to the department being referred to."
          placeholder="e.g. {{order.data.submission.referredToDepartment}}"
        />

        <Divider />

        {/* Patient Details */}
        <BlockValueTemplateField
          fieldName="config.payload.submission.patientPhoneNumber"
          label="Patient Phone Number"
          helperText="Enter the reference to the patient's phone number."
          placeholder="e.g. {{order.data.submission.patientPhoneNumber}}"
        />

        <Divider />

        {/* Order Job Params */}
        <OrderJobParamsFormFields />
      </Stack>
    );
  },
};
